import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, UploadCloud, CheckCircle2, ArrowLeft, ArrowRight, Loader2, FileText, Phone } from "lucide-react";
import { CONTACT } from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILING_STATUSES = ["Single", "Married Filing Jointly", "Married Filing Separately", "Head of Household", "Qualifying Widow(er)"];
const CURRENT_YEAR = new Date().getFullYear();
const TAX_YEARS = [String(CURRENT_YEAR), String(CURRENT_YEAR - 1), String(CURRENT_YEAR - 2), String(CURRENT_YEAR - 3), "Prior years"];
const HELP_OPTIONS = [
  "Individual tax return",
  "Small business taxes / accounting",
  "Tax planning",
  "Financial consulting",
  "Bookkeeping service",
  "Not sure yet",
];

const inputCls =
  "w-full bg-[#F7F8FA] border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-[#1F2933] placeholder-[#5F6B7A] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/70 focus:border-[#C9A227] transition";

const Stepper = ({ step }) => (
  <div data-testid="intake-progress" className="flex items-center gap-2 mb-6">
    {["Your Details", "What You Need", "Confirm"].map((label, i) => {
      const n = i + 1;
      const active = step === n;
      const done = step > n;
      return (
        <div key={label} className="flex items-center gap-2 flex-1 last:flex-none">
          <div className="flex flex-col items-center gap-1">
            <div
              data-testid={`intake-step-indicator-${n}`}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                done ? "bg-[#0B2545] text-white" : active ? "bg-[#C9A227] text-[#0B2545]" : "bg-white text-slate-600 border border-slate-300"
              }`}
            >
              {done ? "✓" : n}
            </div>
            <span className={`text-[10px] uppercase tracking-wide whitespace-nowrap ${active ? "text-[#86680A]" : "text-slate-500"}`}>
              {label}
            </span>
          </div>
          {n < 3 && <div className={`h-px flex-1 mb-4 ${done ? "bg-emerald-600" : "bg-slate-200"}`} />}
        </div>
      );
    })}
  </div>
);

export default function IntakeModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", tax_year: String(CURRENT_YEAR), filing_status: "Single",
    help_needed: [], notes: "",
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const toggleHelp = (opt) => {
    set("help_needed", form.help_needed.includes(opt)
      ? form.help_needed.filter((o) => o !== opt)
      : [...form.help_needed, opt]);
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address";
      if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone number";
    }
    if (step === 2 && form.help_needed.length === 0) e.help_needed = "Please select at least one option";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const oversized = incoming.filter((f) => f.size > 10 * 1024 * 1024);
    if (oversized.length) setErrors((e) => ({ ...e, files: `${oversized[0].name} is larger than 10MB` }));
    else setErrors((e) => ({ ...e, files: undefined }));
    setFiles((prev) => [...prev, ...incoming.filter((f) => f.size <= 10 * 1024 * 1024)]);
  };

  const submit = async () => {
    setSubmitting(true);
    setErrors({});
    try {
      const data = new FormData();
      data.append("full_name", form.full_name);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("tax_year", form.tax_year);
      data.append("filing_status", form.filing_status);
      data.append("help_needed", form.help_needed.join(", "));
      data.append("notes", form.notes);
      files.forEach((f) => data.append("files", f));
      const res = await axios.post(`${API}/intake`, data);
      setResult(res.data);
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || "Something went wrong. Please try again or email us at marizematax@gmail.com." });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1); setFiles([]); setResult(null); setErrors({});
    setForm({ full_name: "", email: "", phone: "", tax_year: String(CURRENT_YEAR), filing_status: "Single", help_needed: [], notes: "" });
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="intake-modal"
        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white border border-slate-200 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[92vh] overflow-y-auto p-6 sm:p-8"
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#86680A]">Marizema Tax Service LLC</p>
              <h3 className="font-serif-display text-xl sm:text-2xl font-semibold text-[#0B2545] mt-1">Start Your Tax Return</h3>
              <a data-testid="intake-phone-link" href={CONTACT.phoneTel}
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#86680A] transition-colors">
                <Phone size={13} className="text-[#86680A]" /> Prefer to talk? Call {CONTACT.phoneDisplay}
              </a>
            </div>
            <button data-testid="intake-btn-close" onClick={onClose} className="text-slate-500 hover:text-[#0B2545] p-1" aria-label="Close">
              <X size={22} />
            </button>
          </div>

          {result ? (
            <div data-testid="intake-success" className="text-center py-6">
              <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-4" />
              <h4 className="font-serif-display text-xl font-semibold text-[#0B2545] mb-2">We'll be in touch within 24 hours</h4>
              <p className="text-sm text-[#1F2933] mb-1">{result.message}</p>
              <p className="text-sm text-slate-500 mb-1">
                Reference: <span data-testid="intake-reference" className="text-[#86680A] font-mono font-semibold">{result.reference}</span>
              </p>
              {result.documents_received > 0 && (
                <p className="text-xs text-slate-500">{result.documents_received} document(s) received securely.</p>
              )}
              <button data-testid="intake-btn-done" onClick={() => { reset(); onClose(); }}
                className="mt-6 px-6 py-2.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-semibold hover:bg-[#D4AF37] transition-colors">
                Done
              </button>
            </div>
          ) : (
            <>
              <Stepper step={step} />
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 16, opacity: 0 }} transition={{ duration: 0.18 }}>
                  {step === 1 && (
                    <div data-testid="intake-modal-step-1" className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Full name *</label>
                        <input data-testid="intake-input-fullname" className={inputCls} placeholder="e.g. Marshet Mekonnen"
                          value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                        {errors.full_name && <p data-testid="intake-error-fullname" className="text-xs text-red-600 mt-1">{errors.full_name}</p>}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Email *</label>
                          <input data-testid="intake-input-email" type="email" className={inputCls} placeholder="you@example.com"
                            value={form.email} onChange={(e) => set("email", e.target.value)} />
                          {errors.email && <p data-testid="intake-error-email" className="text-xs text-red-600 mt-1">{errors.email}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Phone *</label>
                          <input data-testid="intake-input-phone" type="tel" className={inputCls} placeholder="(813) 555-0000"
                            value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                          {errors.phone && <p data-testid="intake-error-phone" className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Tax year *</label>
                          <select data-testid="intake-select-taxyear" className={inputCls} value={form.tax_year} onChange={(e) => set("tax_year", e.target.value)}>
                            {TAX_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Filing status *</label>
                          <select data-testid="intake-select-filingstatus" className={inputCls} value={form.filing_status} onChange={(e) => set("filing_status", e.target.value)}>
                            {FILING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div data-testid="intake-modal-step-2" className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#1F2933] mb-2">What do you need help with? *</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {HELP_OPTIONS.map((opt) => (
                            <button key={opt} type="button"
                              data-testid={`intake-help-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => toggleHelp(opt)}
                              className={`text-left text-sm px-3.5 py-2.5 rounded-lg border transition-colors ${
                                form.help_needed.includes(opt)
                                  ? "border-[#C9A227] bg-[#C9A227]/15 text-[#0B2545]"
                                  : "border-slate-200 bg-[#F7F8FA] text-[#1F2933] hover:border-slate-500"
                              }`}>
                              {opt}
                            </button>
                          ))}
                        </div>
                        {errors.help_needed && <p data-testid="intake-error-help" className="text-xs text-red-600 mt-1.5">{errors.help_needed}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Upload documents (W-2, 1099, receipts — PDF or image, max 10MB each)</label>
                        <label data-testid="intake-file-dropzone"
                          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-[#C9A227]/70 rounded-xl py-8 cursor-pointer transition-colors bg-[#F7F8FA]"
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
                          <UploadCloud size={28} className="text-[#86680A]" />
                          <span className="text-sm text-[#1F2933]">Drag & drop files here or <span className="text-[#86680A] underline">browse</span></span>
                          <input data-testid="intake-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden"
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                        </label>
                        {files.length > 0 && (
                          <ul data-testid="intake-file-list" className="mt-3 space-y-1.5">
                            {files.map((f, i) => (
                              <li key={i} className="flex items-center justify-between text-xs bg-[#F7F8FA] border border-slate-200 rounded-lg px-3 py-2">
                                <span className="flex items-center gap-2 text-[#1F2933] truncate"><FileText size={14} className="text-[#86680A] shrink-0" />{f.name}</span>
                                <button type="button" data-testid={`intake-file-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}
                                  className="text-slate-500 hover:text-red-600 ml-2 shrink-0"><X size={14} /></button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {errors.files && <p data-testid="intake-error-files" className="text-xs text-red-600 mt-1.5">{errors.files}</p>}
                        <p className="text-[11px] text-slate-500 mt-2">Optional — you can also email documents later to marizematax@gmail.com.</p>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div data-testid="intake-modal-step-3" className="space-y-4">
                      <div className="bg-[#F7F8FA] border border-slate-200 rounded-xl p-4 space-y-2 text-sm">
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Name</span><span className="text-[#0B2545] text-right">{form.full_name}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Email</span><span className="text-[#0B2545] text-right break-all">{form.email}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Phone</span><span className="text-[#0B2545] text-right">{form.phone}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Tax year</span><span className="text-[#0B2545] text-right">{form.tax_year}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Filing status</span><span className="text-[#0B2545] text-right">{form.filing_status}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Help with</span><span className="text-[#0B2545] text-right">{form.help_needed.join(", ")}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Documents</span><span className="text-[#0B2545] text-right">{files.length} file(s)</span></div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Anything else we should know? (optional)</label>
                        <textarea data-testid="intake-input-notes" rows={3} className={inputCls} placeholder="Special circumstances, questions, or notes…"
                          value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                      </div>
                      <p className="text-xs text-slate-500 bg-[#C9A227]/10 border border-[#C9A227]/40 rounded-lg px-3.5 py-2.5">
                        Once submitted, a member of our Tampa team will review your information and <span className="text-[#86680A] font-semibold">be in touch within 24 hours</span>.
                      </p>
                      {errors.submit && <p data-testid="intake-error-submit" className="text-xs text-red-600">{errors.submit}</p>}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between mt-7">
                <button data-testid="intake-btn-prev" onClick={() => setStep((s) => s - 1)} disabled={step === 1}
                  className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-full border border-slate-600 text-[#1F2933] hover:border-[#C9A227]/60 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                {step < 3 ? (
                  <button data-testid="intake-btn-next" onClick={() => validateStep() && setStep((s) => s + 1)}
                    className="flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-full bg-[#C9A227] text-[#0B2545] hover:bg-[#D4AF37] transition-colors">
                    Next <ArrowRight size={16} />
                  </button>
                ) : (
                  <button data-testid="intake-btn-submit" onClick={submit} disabled={submitting}
                    className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full bg-[#C9A227] text-[#0B2545] hover:bg-[#D4AF37] disabled:opacity-60 transition-colors">
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    {submitting ? "Submitting…" : "Submit Intake"}
                  </button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
